// src/utils/axiosInstance.ts
import axios from 'axios';
import { BASE_URL } from '../config/constants';

const axiosInstance = axios.create({
    baseURL: BASE_URL,
});

// Helper: decode JWT payload and check if it is expired
function isJwtExpired(token: string): boolean {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return typeof payload.exp === 'number' && payload.exp * 1000 < Date.now();
    } catch {
        return false; // unparseable — let the server decide
    }
}

// Attach token & domain to request (check both storages)
axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const domain = localStorage.getItem('selectedDomain') || sessionStorage.getItem('selectedDomain');

        if (token) {
            // Reject before making the network call if the token is already expired
            if (isJwtExpired(token)) {
                for (const s of [localStorage, sessionStorage]) {
                    s.removeItem('token');
                    s.removeItem('user');
                    s.removeItem('selectedDomain');
                }
                localStorage.removeItem('onboarding_progress');
                window.dispatchEvent(new CustomEvent('auth-expired'));
                return Promise.reject(new axios.Cancel('Token expired'));
            }
            config.headers.Authorization = `Bearer ${token}`;
        }
        if (domain) {
            config.headers['X-Domain'] = domain;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// Handle 401 Unauthorized and 403 Inactive globally
axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Clear session data from both storages
            for (const s of [localStorage, sessionStorage]) {
                s.removeItem('token');
                s.removeItem('user');
                s.removeItem('selectedDomain');
            }
            localStorage.removeItem('onboarding_progress');

            // Notify App to handle logout via state (no full page reload)
            window.dispatchEvent(new CustomEvent('auth-expired'));
        }

        // Detect account/domain deactivation (403 with inactive message)
        if (
            error.response?.status === 403 &&
            typeof error.response?.data?.message === 'string' &&
            error.response.data.message.toLowerCase().includes('inactive')
        ) {
            window.dispatchEvent(new CustomEvent('account-deactivated', {
                detail: { message: error.response.data.message },
            }));
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
